import React, { Fragment, type HTMLAttributes, useContext, useDebugValue, useMemo } from 'react';

import { toArray } from '../helpers';
import type { Language } from '../services/Language';
import { type TFunction, type TranslationContext, useTranslationContext } from '../TranslationProvider';

import type { RegionSwitchChildrenProps } from './Restriction';

interface TContext {
  context: string[];
  prefix: string;
  ns: string;
  prefixSeparator: string;
}

const context = React.createContext<TContext>({
  context: [],
  prefix: null,
  ns: null,
  prefixSeparator: '.',
});

interface Props extends HTMLAttributes<HTMLSpanElement> {
  id: string;
  as?: string;
  data?: (Record<string, unknown> & { count?: number }) | unknown[];
  count?: number;
  context?: string;
  html?: boolean;
  empty?: boolean;
  multiline?: boolean;
  optional?: boolean;
  noBreak?: boolean;
  defaultValue?: string;
  fallback?: string | string[];
}

type UseTranslationResponse = Partial<TContext> &
  TranslationContext & {
    t: TFunction;
    language: Language;
  };

/**
 * Custom hook that provides translation functionality.
 *
 * @param {string} [ns] - Optional namespace to be used for translations.
 * @returns {UseTranslationResponse} An object that includes the translation function `t`,
 * the current language, and translation context details like `context`, `prefix`, and `ns`.
 *
 * The hook utilizes the translation context from `useTranslationContext` and `context` to
 * configure translation options, allowing translations to be scoped and parameterized
 * using context, prefixes, and namespaces.
 */
export function useTranslation(ns?: string): UseTranslationResponse {
  const ctx = useTranslationContext();
  const tCtx = useContext(context);

  const t: TFunction = (id, options = {}) =>
    ctx.translate(id, {
      ...options,
      context: options.context ?? tCtx.context,
      prefix: tCtx.prefix,
      prefixSeparator: tCtx.prefixSeparator,
      ns: options.ns ?? ns ?? tCtx.ns,
    });

  return {
    ...ctx,
    t,
    language: ctx.language,
    context: tCtx.context,
    prefix: tCtx.prefix,
    prefixSeparator: tCtx.prefixSeparator,
    ns: ns ?? tCtx.ns,
  };
}

/**
 * A convenience component for translating text.
 *
 * It uses the `useTranslation` hook and passes the props to `t` from the hook.
 *
 * @param {Props & RegionSwitchChildrenProps} props
 * @property {string} id - The id of the translation.
 * @property {boolean} [html=false] - Whether to render the translation as HTML.
 * @property {Record<string, unknown> & {count?: number}} [data] - Data to be used
 * for interpolation in the translation.
 * @property {string} [context] - Context to be used for the translation.
 * @property {boolean} [empty=false] - Whether to render nothing if the translation is equal to the id.
 * @property {boolean} [multiline=false] - Whether to render the translation as a block of text.
 * @property {boolean} [optional=false] - Whether to render nothing if the translation is not found.
 * @property {boolean} [noBreak=false] - Whether to render the translation without line breaks.
 * @property {string | React.ExoticComponent} [as] - The component to render the translation as.
 * @property {string | string[]} [fallback] - A fallback translation to use if the translation is not found.
 * @property {RegionSwitchChildrenProps} _region - Internal prop, do not use.
 * @property {RegionSwitchChildrenProps} _force - Internal prop, do not use.
 * @property {React.ComponentPropsWithoutRef<React.ElementType<any>>} ...props - Any other props will be passed to the rendered component.
 *
 * @example
 * <T id="hello" />
 * <T id="hello" data={{name: 'John'}} />
 * <T id="hello" context="greeting" />
 * <T id="hello" empty />
 * <T id="hello" multiline />
 * <T id="hello" optional />
 * <T id="hello" noBreak />
 * <T id="hello" as="h1" />
 * <T id="hello" fallback="Hello" />
 **/
export default function T({
  id,
  html,
  data,
  context,
  empty,
  multiline,
  optional,
  noBreak,
  as,
  fallback,
  _region,
  _force,
  ...props
}: Props & RegionSwitchChildrenProps) {
  const ctx = useTranslation();

  if (optional && typeof id !== 'string') return null;

  const keys = [id];
  if (fallback) {
    keys.push(...toArray(fallback));
  }
  let defaultValue = props.defaultValue;
  if (empty) {
    defaultValue = id;
  } else if (optional) {
    defaultValue ??= id;
  }
  let count = props.count;
  if (data && typeof data === 'object' && 'count' in data) {
    count = data.count;
  }
  const tOptions = { data, count, default: defaultValue, context };
  useDebugValue(keys, keys => ctx.resolveKeys(keys, tOptions).join(', '));
  let translation = ctx.t(keys, tOptions);

  if (!optional && empty && translation === id && process.env.GATSBY_TRANSLATION_DEBUG !== 'true') {
    return null;
  }

  if (multiline) {
    translation = translation.replace(/(\\n|\n)/g, '<br />');
    html = true;
  } else if (noBreak) {
    translation = translation.replace(/(\\n|\n|\s)/g, '&nbsp;');
    html = true;
  }

  let Component:
    | string
    | React.ExoticComponent<{
        children?: React.ReactNode | undefined;
        dangerouslySetInnerHTML?: { __html: string };
      }> = as ?? Fragment;
  if (Object.keys(props).length || html) {
    Component = as ?? 'span';
  }
  if (html) {
    return <Component {...props} dangerouslySetInnerHTML={{ __html: translation }} />;
  }

  // @ts-expect-error TS2322: Type { children: string; count?: number; defaultValue?: string; defaultChecked?: boolean; suppressContentEditableWarning?: boolean; suppressHydrationWarning?: boolean; accessKey?: string; autoCapitalize?: "off" | ... 5 more ... | (string & {});  ... 251 more ...; onTransitionEndCapture?: TransitionEventHandler<...>; }
  // is not assignable to type {   children?: ReactNode;   dangerouslySetInnerHTML?: {     __html: string;   }; }
  return <Component {...props}>{translation}</Component>;
}

/**
 * A hook that returns a translation function that returns the original `text` if a translation doesn't exist.
 * Useful for displaying a fallback text if the translation is not available.
 *
 * @param text The text to translate
 * @param data Additional data to pass to the translation function
 * @returns A function that takes a single argument `text` and returns its translation, or the original `text` if a translation doesn't exist.
 **/
export function useOptionalTranslate(text: string, data: Record<string, string> = {}) {
  const { t } = useTranslation();
  return useMemo(() => t(text, { data, default: text }), [text, data]);
}

interface TContextProps {
  name: string;
}

export interface TProviderProps {
  context?: string | string[];
  prefix?: string;
  ns?: string;
  prefixSeparator?: string;
}

/**
 * Provides a translation context that can be used by the `useTranslation` hook.
 *
 * The context is an object with the following properties:
 * - `context`: An array of context strings to be used for translation.
 * - `prefix`: A string prefix to be used for translation.
 * - `prefixSeparator`: The separator to be used when combining the prefix with the context.
 * - `ns`: The namespace to be used for translation.
 *
 * The component will merge the props with the parent context, so you can override any of the properties.
 *
 * @param props The properties to be passed to the context provider.
 * @param props.context The context to be used for translation.
 * @param props.prefix The prefix to be used for translation.
 * @param props.prefixSeparator The separator to be used when combining the prefix with the context.
 * @param props.ns The namespace to be used for translation.
 * @param props.children The children to be rendered within the context provider.
 * @returns A JSX element that wraps the children with the context provider.
 **/
export function TProvider(props: React.PropsWithChildren<TProviderProps>) {
  const parent = useContext(context);
  const value = { ...parent };
  if (props.context) {
    value.context = [...(value.context ?? []), ...toArray(props.context)];
  }
  if (props.prefix) {
    if (props.prefixSeparator) {
      value.prefixSeparator = props.prefixSeparator;
    }
    value.prefix = value.prefix ? `${value.prefix}${value.prefixSeparator}${props.prefix}` : props.prefix;
  }
  if (props.ns) {
    value.ns = props.ns;
  }
  return <context.Provider value={value}>{props.children}</context.Provider>;
}

/**
 * A shorthand for `TProvider` that sets the translation context.
 *
 * The `TContext` component takes a single prop `name` and passes it as the `context` prop to the `TProvider`.
 * This allows you to set the translation context without having to use the `TProvider` directly.
 *
 * @example
 * <TContext name="my.context">...</TContext>
 *
 * @param props The properties to be passed to the component.
 * @param props.name The name of the context to be used for translation.
 * @param props.children The children to be rendered within the context provider.
 * @returns A JSX element that wraps the children with the context provider.
 **/
export function TContext({ name, children }: React.PropsWithChildren<TContextProps>) {
  return <TProvider context={name}>{children}</TProvider>;
}

interface TPrefixProps extends TContextProps {
  separator?: string;
}

/**
 * A React component that provides a translation prefix context to its children.
 *
 * This component uses the `TProvider` to set a prefix and a prefix separator for translation keys.
 * It allows nested translations to inherit and extend the prefix, enabling organized and scoped translations.
 *
 * @param {TPrefixProps} props - The properties for the prefix context.
 * @param {string} props.name - The prefix to be used for translation keys.
 * @param {string} [props.separator] - An optional separator to be used when combining the prefix with other translation keys.
 * @param {React.ReactNode} props.children - The child components to be rendered within the provider.
 * @returns {JSX.Element} A JSX element that wraps the children with the prefix context provider.
 **/
export function TPrefix({ name, separator, children }: React.PropsWithChildren<TPrefixProps>) {
  return (
    <TProvider prefix={name} prefixSeparator={separator}>
      {children}
    </TProvider>
  );
}

/**
 * A React component that wraps its children with a context provider
 * that sets the translation namespace to the given `name`.
 *
 * @param props.name The name of the namespace to use for translation.
 * @param props.children The children to be rendered within the context provider.
 * @returns A JSX element that wraps the children with the context provider.
 **/
export function TNamespace({ name, children }: React.PropsWithChildren<TContextProps>) {
  return <TProvider ns={name}>{children}</TProvider>;
}

export interface WithTranslationProps {
  translation: TranslationContext;
}

/**
 * A Higher-Order Component that injects the translation context into the wrapped component.
 *
 * The wrapped component will receive a `translation` prop with the current translation context.
 *
 * @example
 * import { withTranslation } from '@craigmiller160/react-translate';
 *
 * const MyComponent = () => {
 *   const { translate } = this.props.translation;
 *   return <div>{translate('hello.world')}</div>;
 * };
 *
 * export default withTranslation()(MyComponent);
 *
 * @param WrappedComponent The component to be wrapped with the translation context.
 * @returns A new component that injects the translation context into the wrapped component.
 **/
export function withTranslation() {
  return WrappedComponent => {
    const Component = props => {
      const ctx = useTranslationContext();
      return <WrappedComponent {...props} translation={ctx} />;
    };
    Component.displayName = `withTranslation(${WrappedComponent.displayName})`;
    return Component;
  };
}
